/**
 * When each money account was last counted (`cuentasContadas`, a setting only the books write):
 * general cash at a count of the safe, the bank, the dollar account and the wallet at a statement.
 *
 * A count finds what the money really is and books whatever the books had wrong as a difference.
 * So a deposit, money for the owner, a supplier's payment or an expense recorded before it is
 * past voiding: voided now, the money would move on the books a second time for what the count
 * already put right, and the next count would find it the other way. And general cash never
 * counted is where the safe starts: its first count is its opening balance, not a surplus.
 *
 * In the log's order, not by date: a deposit written down after the count of that day was not
 * counted by it. Each by its place in the log (`lugarEnElLog`). A statement also by date: it is
 * squared against the books on the day it reads to, so what is dated after that it never saw,
 * whenever it was written down (`estadosDeCuenta`): the September statement squared on 3 October
 * did not find a deposit of 2 October.
 */

import { esCuentaDeBanco, isStored, sumarDias, type CuentaBanco, type Envelope, type Settings, type StoredEnvelope } from '@abastece/shared';
import { ACCOUNTS, type AccountKey } from '../ledger/accounts.js';
import type { Effect } from '../state.js';
import { nombreDeCuentaBanco } from './bancos.js';

/**
 * From here up, this device's own events the server has not numbered yet; below, the server's
 * numbers. No shop gets near 2^52 events, and every place stays a safe integer.
 */
const SIN_NUMERAR = 2 ** 52;

/**
 * An event's place in the log: the server's number, or, for one of this device's own not numbered
 * yet, after every numbered one, in the order the device recorded them. Finer than `orderKey`,
 * which puts all of those in one place: a deposit and the count after it, both still on the phone
 * (always, in the practice shop), would be neither before nor after the other. The server numbers
 * a device's events in the device's own order, after whatever the device had already pulled, so
 * each keeps its side of every other once numbered.
 */
export function lugarEnElLog(e: Envelope | StoredEnvelope): number {
  return isStored(e) ? e.serverSeq : SIN_NUMERAR + e.deviceSeq;
}

/** The books' note that `cuenta` was counted by the event at `lugar` (its `rev`, for the note's own). */
export function contada(config: Settings, cuenta: AccountKey, lugar: number, rev: number): Effect {
  return { kind: 'config.set', clave: 'cuentasContadas', valor: { ...config.cuentasContadas, [ACCOUNTS[cuenta].code]: lugar }, rev };
}

/**
 * The books' note of a statement squared: `cuenta` counted by the event at `lugar`, up to `fecha`,
 * the dollar account's at `dolares`. The statements before it that read to no later a day drop
 * out: this one found everything they did.
 */
export function conciliada(
  config: Settings,
  cuenta: CuentaBanco | 'BILLETERA_MOVIL',
  estado: { lugar: number; fecha: string; dolares?: number },
  rev: number,
): Effect[] {
  const codigo = ACCOUNTS[cuenta].code;
  const siguen = (config.estadosDeCuenta[codigo] ?? []).filter((e) => e.fecha > estado.fecha);
  return [
    contada(config, cuenta, estado.lugar, rev),
    { kind: 'config.set', clave: 'estadosDeCuenta', valor: { ...config.estadosDeCuenta, [codigo]: [...siguen, estado] }, rev },
  ];
}

/** Whether `cuenta` has ever been counted. */
export function yaContada(config: Settings, cuenta: AccountKey): boolean {
  return ACCOUNTS[cuenta].code in config.cuentasContadas;
}

/**
 * Whether `cuenta` was counted after the event at `lugar`, dated `fecha`: what that event moved,
 * the count found. A statement, only if it reads to that day or later.
 */
export function contadaDespues(config: Settings, cuenta: AccountKey, lugar: number, fecha: string): boolean {
  const codigo = ACCOUNTS[cuenta].code;
  const estados = config.estadosDeCuenta[codigo];
  if (estados) return estados.some((e) => e.lugar > lugar && e.fecha >= fecha);
  return (config.cuentasContadas[codigo] ?? -Infinity) > lugar;
}

/**
 * The day a payment from the queue goes into its account once it is seen there (2026-10-20): the
 * day it was taken, unless a statement of that account reading to that day or later was squared
 * already. That statement found the account without it, still in the queue: it came in after the
 * statement's day, so on the day it was seen, and never on a day a statement already read. Dated
 * back, the account read more for that day than its squared statement says. One the statement did
 * show is confirmed before squaring it (`Conciliar`, «Cuadrar la semana»): squared first, the
 * difference already took it in, and confirming it after puts it in again, until the next statement.
 */
export function diaDeLaConfirmacion(config: Settings, cuenta: AccountKey, fecha: string, visto: string): string {
  const leidos = (config.estadosDeCuenta[ACCOUNTS[cuenta].code] ?? []).map((e) => e.fecha).filter((f) => f >= fecha);
  if (leidos.length === 0) return fecha;
  const ultimo = leidos.reduce((a, b) => (b > a ? b : a));
  return visto > ultimo ? visto : sumarDias(ultimo, 1);
}

/**
 * Why `que` («ese depósito») is past voiding: `cuenta` was counted after it. A bank account by the
 * owner's name for it, with the shop's settings (`config`).
 */
export function yaLaContaron(cuenta: AccountKey, que: string, config?: Settings): string {
  const cual =
    cuenta === 'BILLETERA_MOVIL'
      ? 'la billetera'
      : esCuentaDeBanco(cuenta) && config
        ? nombreDeCuentaBanco(config, cuenta)
        : cuenta === 'BANCO_DOLARES' || cuenta.startsWith('BANCO_DOLARES')
          ? 'la cuenta en dólares'
          : 'el banco';
  const como =
    cuenta === 'CAJA_FUERTE'
      ? 'ya se contó la caja fuerte: ese conteo ya anotó la diferencia'
      : `ya se comparó ${cual} con su estado de cuenta: ya se anotó la diferencia`;
  return `Después de ${que} ${como}`;
}
